<template>
  <OverlaySpinner :overlay="true" v-if="isLoading" />
  <div class="compsearch-page-container">
    <div class="left-panels-container">
      <div class="comp-search-modal" :class="{ 'open': isLeftOpen }">
        <CompSearch 
          @resize-sidebar-comp-search="openLeftModal" 
          @comp-search-submit="handleCompSearchSubmit"
          @map-reset="resetMap"
          @filters-cleared="handleFiltersCleared"
        />
      </div>
      <!-- CompSummary only renders when isRightVisible is true -->
      <div v-if="isRightVisible" id="comp-summary-modal" class="comp-summary-modal" :class="{ 'open': isRightOpen }">
        <CompSummary ref="compSummaryRef" @resize-modal="toggleRightModal" />
      </div>
    </div>
    <div id="home-map" data-test-id="home-map" class="map-container" :class="{ 'left-open': isLeftOpen, 'both-open': isLeftOpen && isRightOpen }">
      <div id="map" style="height:100%; width:100%;"></div>
    </div>
  </div>
</template>

<script setup>
/* eslint no-undef: 0 */
import { onMounted, onActivated, ref, watch, nextTick } from 'vue';
import { useCompSetStore } from "@/stores/comp_set_store";
import { useErrorStore } from "@/stores/error_store";
import { useAuthStore } from "@/stores/auth_store";
import { debounce, inRange, numberFormat, currencyFormat, axiosQuery } from "@/assets/js/utils";
import CompSearch from "@/components/comp-search/CompSearch.vue"
import CompSummary from "@/components/comp-search/CompSummary.vue"
import OverlaySpinner from "@/components/helpers/OverlaySpinner.vue"
import { loadGoogleMapsAPI } from '@/main.js';
import { fetchImage } from "@/assets/js/image_utils";

const compSetStore = useCompSetStore();
const errorStore = useErrorStore();

const metersInMile = 1609.34
let defaultMapRadiusMeters = metersInMile * 7.0 // Default to miles radius for map
let mapRadiusMeters = defaultMapRadiusMeters
let circleCenterLocation = { lat: 40.4000, lng: -74.5000 } // New York/NJ to start
const pinSvgString =
  '<svg xmlns="http://www.w3.org/2000/svg" width="5.6444445mm" height="9.847393mm" viewBox="0 0 20 34.892337" version="1.1" >\n' +
  '  <g transform="translate(-814.59595,-274.38623)">\n' +
  '    <g transform="matrix(1.1855854,0,0,1.1855854,-151.17715,-57.3976)">\n' +
  '      <path d="m 817.11249,282.97118 c -1.25816,1.34277 -2.04623,3.29881 -2.01563,5.13867 0.0639,3.84476 1.79693,5.3002 4.56836,10.59179 0.99832,2.32851 2.04027,4.79237 3.03125,8.87305 0.13772,0.60193 0.27203,1.16104 0.33416,1.20948 0.0621,0.0485 0.19644,-0.51262 0.33416,-1.11455 0.99098,-4.08068 2.03293,-6.54258 3.03125,-8.87109 2.77143,-5.29159 4.50444,-6.74704 4.56836,-10.5918 0.0306,-1.83986 -0.75942,-3.79785 -2.01758,-5.14062 -1.43724,-1.53389 -3.60504,-2.66908 -5.91619,-2.71655 -2.31115,-0.0475 -4.4809,1.08773 -5.91814,2.62162 z" style="display:inline;opacity:1;fill:#4A5FD5;fill-opacity:1;stroke:#3b73a8;stroke-width:1;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:1"/>\n' +
  '      <circle r="3.0355" cy="288.25278" cx="823.03064" style="display:inline;opacity:1;fill:#fff;fill-opacity:1;stroke-width:0"/>\n' +
  '    </g>\n' +
  '  </g>\n' +
  '</svg>\n';
const fadedPinSvgString =
  '<svg xmlns="http://www.w3.org/2000/svg" width="5.6444445mm" height="9.847393mm" viewBox="0 0 20 34.892337" version="1.1" >\n' +
  '  <g transform="translate(-814.59595,-274.38623)">\n' +
  '    <g transform="matrix(1.1855854,0,0,1.1855854,-151.17715,-57.3976)">\n' +
  '      <path d="m 817.11249,282.97118 c -1.25816,1.34277 -2.04623,3.29881 -2.01563,5.13867 0.0639,3.84476 1.79693,5.3002 4.56836,10.59179 0.99832,2.32851 2.04027,4.79237 3.03125,8.87305 0.13772,0.60193 0.27203,1.16104 0.33416,1.20948 0.0621,0.0485 0.19644,-0.51262 0.33416,-1.11455 0.99098,-4.08068 2.03293,-6.54258 3.03125,-8.87109 2.77143,-5.29159 4.50444,-6.74704 4.56836,-10.5918 0.0306,-1.83986 -0.75942,-3.79785 -2.01758,-5.14062 -1.43724,-1.53389 -3.60504,-2.66908 -5.91619,-2.71655 -2.31115,-0.0475 -4.4809,1.08773 -5.91814,2.62162 z" style="display:inline;opacity:1;fill:#FEB465;fill-opacity:0.8;stroke:#3b73a8;stroke-width:1;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:1"/>\n' +
  '      <circle r="3.0355" cy="288.25278" cx="823.03064" style="display:inline;opacity:1;fill:#fff;fill-opacity:1;stroke-width:0"/>\n' +
  '    </g>\n' +
  '  </g>\n' +
  '</svg>\n';

const isGoogleMapsMounted = ref(false)
const isLoading = ref(true)
const selectedPlaceName = ref('')
const compSummaryRef = ref(null)

/**
 * Initializes Google Maps with a delay, loads the Google Maps API, and sets up the map.
 * If the queryMapExpanded is not empty, it opens the right modal and shows the CompSet summary modal.
 * Handles errors if the Google Maps API fails to load.
 */
onMounted(() => {
  // Make sure the map container exists
  const mapContainer = document.getElementById('map');
  if (!mapContainer) {
    console.error('Map container not found');
    errorStore.setError('Map Error', 'Map container element not found', 'error');
    isLoading.value = false;
    return;
  }
  
  // REMOVED: Nuclear fix was hiding CompSummary and preventing it from showing
  // The issue was that inline styles (display:none, etc.) override Vue reactive classes
  // setTimeout(() => {
  //   const allCompSummaryElements = document.querySelectorAll('.side-panel-left, #comp-summary-modal, [data-test-id="comp-summary-panel"]');
  //   allCompSummaryElements.forEach(el => {
  //     el.classList.remove('open');
  //     el.style.display = 'none';
  //     el.style.width = '0px';
  //     el.style.visibility = 'hidden';
  //     el.style.opacity = '0';
  //   });
  //   console.log('🧹 Cleaned up any leftover CompSummary elements on page load');
  // }, 100);

  // Load Google Maps API with a slight delay to ensure DOM is ready
  setTimeout(() => {
    loadGoogleMapsAPI
      .then(() => {
        console.log('Google Maps API loaded successfully');
        
        // Check if Google Maps API is properly loaded
        if (typeof google === 'undefined' || !google.maps || !google.maps.ControlPosition) {
          throw new Error('Google Maps API not properly initialized');
        }
        
        // Initialize the map
        loadGoogleMaps();
        
        // 🔧 FIX: Restore markers when navigating via header links
        // Give the map a moment to fully initialize, then check for stored projects
        setTimeout(() => {
          const includedProjects = compSetStore.includedProjects;
          if (includedProjects && includedProjects.length > 0 && markers.length === 0) {
            console.log(`🔄 [onMounted] Restoring ${includedProjects.length} markers after header navigation`);
            includedProjects.forEach(project => {
              processProject(project);
            });
            console.log(`✅ [onMounted] Restored ${markers.length} markers to map`);
          }
        }, 1500); // Delay to ensure map is fully ready
        
        // Handle query parameters
        const queryMapExpanded = compSetStore.getQueryMapExpanded;
        const hasQueryMapExpanded = queryMapExpanded && queryMapExpanded.trim() !== '';

        if (hasQueryMapExpanded) {
          // Don't auto-open second left panel on page load - let user trigger it
          // isRightOpen.value = true;
          // Don't auto-show CompSummary panel on page load - let user trigger it
          // showCompSetSummaryModal();
        } else {
          isRightOpen.value = false;
        }
        
        isLoading.value = false;
      })
      .catch(error => {
        console.error('Error loading Google Maps API:', error);
        errorStore.setError('Google Maps Error', 'Failed to load Google Maps: ' + error.message, 'error');
        isLoading.value = false;
      });
  }, 500); // Increased delay to ensure DOM is fully loaded
})

/**
 * 🔧 FIX: Re-add markers when navigating back to this page
 * When user navigates to Budget page and back, markers disappear even though
 * data is still in the store. This hook detects when the component is reactivated
 * and re-renders the markers if they're missing.
 */
onActivated(() => {
  console.log('🔄 HomeView activated - checking if markers need to be restored');
  
  // Check if we have projects in the store but no markers on the map
  const includedProjects = compSetStore.includedProjects;
  
  if (includedProjects && includedProjects.length > 0 && markers.length === 0) {
    console.log(`🔄 Restoring ${includedProjects.length} markers to map`);
    
    // Re-add all markers for the existing projects
    includedProjects.forEach(project => {
      processProject(project);
    });
    
    console.log(`✅ Restored ${markers.length} markers`);
  } else if (markers.length > 0) {
    console.log(`✅ Markers already present (${markers.length}), no restoration needed`);
  } else {
    console.log('ℹ️ No projects to restore markers for');
  }
});

// Declare variables for global use in this file
let map
let markers = []
let infoWindows = []
let drawnCircle = null
let drawingManager = null
let mapCenter = []
let mapBounds = []

watch(() => compSetStore.location, (newLocation) => {
  // Update selectedPlaceName if available in the location
  if (newLocation?.formatted_address) {
    selectedPlaceName.value = newLocation.formatted_address;
  }
  
  // Skip reloadMap if this is for administrative boundaries (has types)
  const isAdministrativeSearch = newLocation?.types && 
    (newLocation.types.includes('neighborhood') || 
     newLocation.types.includes('political') ||
     newLocation.types.includes('locality') ||
     newLocation.types.includes('administrative_area_level_1'));
  
  if (isAdministrativeSearch) {
    console.log('🎯 Skipping reloadMap for administrative boundary:', newLocation.formatted_address);
    return;
  }
  
  if (isGoogleMapsMounted.value) {
    reloadMap(newLocation)
  } else {
    loadGoogleMaps()
    reloadMap(newLocation)
  }
});

const debouncedLoadMarkers = debounce(() => {
  loadMarkers();
}, delay); // Adjust the delay as needed

// Watch for changes in the includedProjects property of compSetStore
// When includedProjects changes, call debouncedLoadMarkers to update the markers on the map
// Add intelligent debouncing to prevent excessive reloads
let lastProjectsLength = 0;
watch(() => compSetStore.includedProjects, (newProjects) => {
  // Only reload if the projects actually changed meaningfully
  if (!newProjects || 
      (Array.isArray(newProjects) && newProjects.length === lastProjectsLength && lastProjectsLength > 0)) {
    return; // Skip if no change or same count as before
  }
  lastProjectsLength = Array.isArray(newProjects) ? newProjects.length : 0;
  debouncedLoadMarkers();
});

// When scope filter changes (scope data loaded or cleared), reload markers
watch(() => compSetStore.getScopeFilteredProjectIds, () => {
  debouncedLoadMarkers();
});

// Debounced bounds_changed event handler
let isMapProgrammaticallyUpdating = false;
const debouncedBoundsChanged = debounce(() => {
  // Skip if the map is being programmatically updated (like during reset or autocomplete)
  if (isMapProgrammaticallyUpdating) {
    return;
  }
  
  // Get the northeast and southwest bounds of the map
  const boundsNE = map.getBounds()?.getNorthEast();
  const boundsSW = map.getBounds()?.getSouthWest();
  
  // Store the bounds in an array
  mapBounds = [boundsNE?.lat(), boundsNE?.lng(), boundsSW?.lat(), boundsSW?.lng()];
  
  // Update the map expanded query in the store
  compSetStore.setMapExpandedQuery(`location=${mapBounds.join(',')}`);
  
  // If no circle is drawn, query the whole visible part of the map
  // But skip if we have an administrative boundary polygon query
  const hasPolygonQuery = compSetStore.getMapQuery && compSetStore.getMapQuery.startsWith('polygon=');
  if (boundsNE && boundsSW && !drawnCircle && !hasPolygonQuery) {
    mapCenter = map.getCenter(); // Get the center of the map
    compSetStore.setMapQuery(`location=${mapBounds.join(',')}`); // Update the map query in the store
  }
  
  // Throttle the marker loading to avoid frequent queries - only if no projects are currently loaded
  if ((!compSetStore.includedProjects || compSetStore.includedProjects.length === 0) &&
      (!lastMarkerBeginLoad || (lastMarkerBeginLoad + delay) < Date.now())) {
    lastMarkerBeginLoad = Date.now();
    setTimeout(() => {
      loadMarkers(); // Load markers on the map
    }, delay);
  }
}, delay * 2); // Increase delay to reduce frequency

// Set a small delay on reloading data so we don't query too frequently -- at most every <delay> milliseconds
const delay = 200 // in milliseconds
let lastMarkerBeginLoad = null
function loadGoogleMaps() {
  if (typeof google !== 'undefined' && google.maps && google.maps.ControlPosition) {
    const mapOptions = {
      center: compSetStore.center || circleCenterLocation, // Use the store's center
      zoom: compSetStore.zoom,
      mapId: '683d6c38bff6b81d78e51169', // Google Cloud Map ID for Advanced Markers
      mapTypeId: 'roadmap', // Standard Google Maps - overlays work perfectly
      // Ensure all controls are visible and properly positioned
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      },
      streetViewControl: true,
      streetViewControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      },
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      },
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_RIGHT
      }
    };

    map = new google.maps.Map(document.getElementById('map'), mapOptions);

    // Listen to the zoom_changed event
    google.maps.event.addListener(map, 'zoom_changed', () => {
      compSetStore.setZoom(map.getZoom()); // Update the store
    });

    google.maps.event.addListener(map, 'center_changed', () => {
      compSetStore.setCenter(map.getCenter().toJSON()); // Update the store
    });

    // Listen to any circle event that would move the circle and start over
    google.maps.event.addListener(map, 'bounds_changed', debouncedBoundsChanged);

    // Wait for the map to be fully loaded before initializing the drawing tools
    google.maps.event.addListenerOnce(map, 'idle', () => {
      console.log('Map is fully loaded, initializing drawing tools');
      // Allow circles to be drawn on the map ad hoc
      circleDrawingComponent();
      
      // 🔧 FIX: Restore markers if navigating back with existing data
      const includedProjects = compSetStore.includedProjects;
      if (includedProjects && includedProjects.length > 0 && markers.length === 0) {
        console.log(`🔄 Restoring ${includedProjects.length} markers after navigation`);
        includedProjects.forEach(project => {
          processProject(project);
        });
        console.log(`✅ Restored ${markers.length} markers to map`);
      }
    });

    // AdvancedMarker: https://developers.google.com/maps/documentation/javascript/advanced-markers/basic-customization
    if (compSetStore.location) {
      new google.maps.marker.AdvancedMarkerElement({
        position: compSetStore.location.geometry.location,
        map: map,
      });
    }

    isGoogleMapsMounted.value = true
    
    // Skip reloadMap if location is for administrative boundaries (prevent overwriting polygon query)
    const isAdministrativeSearch = compSetStore.location?.types && 
      (compSetStore.location.types.includes('neighborhood') || 
       compSetStore.location.types.includes('political') ||
       compSetStore.location.types.includes('locality') ||
       compSetStore.location.types.includes('administrative_area_level_1'));

    if (!isAdministrativeSearch) {
      reloadMap(compSetStore.location)
    } else {
      console.log('🎯 Skipping loadGoogleMaps reloadMap for administrative boundary:', compSetStore.location.formatted_address);
    }
  } else {
    errorStore.setError('Google Map Error', 'Unknown error opening Google Maps', 'error')
  }
}

// Initialize the drawing manager for the map
function initializeDrawingManager() {
  // See https://developers.google.com/maps/documentation/javascript/examples/drawing-tools
  // and https://developers.google.com/maps/documentation/javascript/drawinglayer#overview
  drawingManager = new google.maps.drawing.DrawingManager({
    drawingMode: null,
    drawingControl: true,
    drawingControlOptions: {
      position: google.maps.ControlPosition.TOP_CENTER, // Centered for better visibility
      drawingModes: [
        google.maps.drawing.OverlayType.CIRCLE
      ],
      style: google.maps.MapTypeControlStyle.DEFAULT, // Use DEFAULT style for bigger buttons
    },
    circleOptions: {
      strokeColor: '#4A5FD5',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#4A5FD5',
      fillOpacity: 0.25,
      clickable: false,
      draggable: true,
      editable: true,
      zIndex: 1,
    },
  });

  drawingManager.setMap(map);
  console.log('Drawing manager initialized and set on map');

  // Listen to any new circle created by the drawing manager
  // For circles, see https://developers.google.com/maps/documentation/javascript/reference/polygon#Circle
  google.maps.event.addListener(drawingManager, 'circlecomplete', (drawn) => {
    // delete old one in case it exists as we need only 1 circle
    if (drawnCircle) {
      drawnCircle.setMap(null);
    }
    moveCircle(drawn);
    // Listen to any circle event that would move the circle and start over
    google.maps.event.addListener(drawn, 'draggend', () => {
      moveCircle(drawn);
    });
    google.maps.event.addListener(drawn, 'center_changed', () => {
      moveCircle(drawn);
    });
    google.maps.event.addListener(drawn, 'radius_changed', () => {
      moveCircle(drawn);
    });
  });
}

function circleDrawingComponent() {
  console.log('Setting up circle drawing component');
  
  // delete old one in case it exists
  if (drawnCircle) {
    drawnCircle.setMap(null);
    console.log('Removed existing circle');
  }
  
  if (drawingManager) {
    drawingManager.setMap(null);
    drawingManager = null;
    console.log('Removed existing drawing manager');
  }

  // Initialize the drawing manager
  initializeDrawingManager();
  console.log('Drawing manager initialized');
  
  // Force the drawing manager to be visible
  setTimeout(() => {
    if (drawingManager && map) {
      drawingManager.setMap(map);
      console.log('Drawing manager set on map again after timeout');
    }
  }, 1000);
}
function moveCircle(drawn) {
  drawnCircle = drawn
  mapRadiusMeters = drawnCircle.getRadius()
  const lat = drawnCircle.center.lat()
  const lng = drawnCircle.center.lng()

  if (lat && lng) {
    mapCenter = mapRadiusMeters ? [lat, lng, mapRadiusMeters] : [lat, lng]
  }
  const mapQuery = mapCenter ? `location=${mapCenter.join(',')}` : null
  // CompSearch needs to query only what is in the encircled data
  compSetStore.setMapQuery(mapQuery)

  // Improved zoom: Use better bounds calculation and set appropriate zoom level
  const bounds = drawn.getBounds()
  map.fitBounds(bounds)
  
  // After fitting bounds, adjust zoom for better visibility of the circle
  setTimeout(() => {
    const currentZoom = map.getZoom()
    // Zoom out slightly to provide context around the circle
    const adjustedZoom = Math.max(currentZoom - 1, 10) // Minimum zoom of 10
    map.setZoom(adjustedZoom)
    
    // Apply circle constraint with current CompSearch filters
    applyCircleWithFilters()
  }, 100)
}

// Function to apply circle constraint with CompSearch filters
async function applyCircleWithFilters() {
  console.log('🔍 Applying circle constraint with CompSearch filters');
  
  if (!drawnCircle || !mapCenter || mapCenter.length < 3) {
    console.warn('No circle drawn or invalid map center for filtering');
    return;
  }
  
  try {
    // Show loading indicator
    isLoading.value = true;
    
    // Get the current filters from the form store
    const { useFormStore } = await import('@/stores/form_store');
    const formStore = useFormStore();
    const currentFilters = formStore;
    
    console.log('Current filters:', currentFilters);
    console.log('Circle center:', mapCenter[0], mapCenter[1]);
    console.log('Circle radius (meters):', mapRadiusMeters);
    console.log('mapCenter array:', mapCenter);
    console.log('drawnCircle radius:', drawnCircle ? drawnCircle.getRadius() : 'No circle');
    
    // Build query with form filters from CompSearch
    // Include any existing compQuery filters but circle location in mapQuery will override any existing location
    let validFilterProjectOptions = {
      // Add form filters from CompSearch form
      primary_asset_category: currentFilters.assetTypes || [],
      building_use_types_full: currentFilters.buildingUseTypes || [],
      num_floors: currentFilters.numberOfFloors || [],
      total_num_units: currentFilters.numberOfUnits || [],
      actual_end_date: currentFilters.yearBuild || [],
      total_gsf: currentFilters.squareFeet || [],
      parking_count: currentFilters.parkingTypes || [],
      project_construction_types_s: currentFilters.constructionTypes || []
      
      // NOTE: Circle location will be set in mapQuery and will override any existing CompSearch location
    };
    
    // Remove empty filters
    validFilterProjectOptions = Object.entries(validFilterProjectOptions).reduce((acc, [key, value]) => {
      if (value && ((Array.isArray(value) && value.length > 0) || (typeof value === 'string' && value.length > 0))) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    console.log('🔍 Backend query with circle + filters:', validFilterProjectOptions);
    
    // Convert to query string
    const qs = await import('qs');
    const queryString = qs.stringify(validFilterProjectOptions, { arrayFormat: 'brackets' });
    
    // Update store with the query
    compSetStore.setCompQuery(queryString);
    compSetStore.setMapQuery(`location=${mapCenter[0]},${mapCenter[1]},${mapRadiusMeters}`);
    
    // Get the combined query (filters + map location)
    const combinedQuery = compSetStore.getQuery;
    
    console.log('🔍 Filter query string:', queryString);
    console.log('🗺️ Map query set to:', `location=${mapCenter[0]},${mapCenter[1]},${mapRadiusMeters}`);
    console.log('🔗 Combined query:', combinedQuery);
    console.log('📍 EXACT LOCATION STRING BEING SENT:', `${mapCenter[0]},${mapCenter[1]},${mapRadiusMeters}`);
    
    // Get API base and auth token
    const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://167.172.25.255:3000/api/v1/";
    const authStore = useAuthStore();
    const authToken = authStore.getAuthTokenHeader;
    
    console.log('🌐 Making API request to:', `${apiBase}projects?${combinedQuery}`);
    
    // Use axiosQuery like the working CompSearch does
    const { axiosQuery } = await import('@/assets/js/utils');
    
    const projects = await new Promise((resolve, reject) => {
      axiosQuery(
        `${apiBase}projects?${combinedQuery}`,
        authToken,
        (data) => resolve(data),
        (error) => reject(error)
      );
    });
    
    console.log('🎯 Backend Circle + Filters Results:', projects);
    console.log(`Found ${projects.length} projects matching circle + filters`);
    
    // Debug: Log coordinates of first few projects to verify they're in circle
    if (projects.length > 0) {
      console.log('🔍 Sample project coordinates from circle search:');
      projects.slice(0, 5).forEach((project, index) => {
        console.log(`Project ${index + 1}: ${project.address || 'Unknown'} at (${project.latitude}, ${project.longitude})`);
      });
      console.log(`🎯 Circle center: (${mapCenter[0]}, ${mapCenter[1]}) radius: ${mapRadiusMeters}m`);
    }
    
    // Update the store with the results
    compSetStore.setAllProjects(projects);
    compSetStore.setIncludedProjects(projects);
    
    // Set all projects as selected
    const selectedProjects = Object.fromEntries(projects.map(project => [project.id, true]));
    compSetStore.setSelectedProjects(selectedProjects);
    
    // Hide loading indicator
    isLoading.value = false;
    
    // Reload markers to show the filtered results
    loadMarkers();
    
    console.log('✅ Circle + Filters: Updated comp set store with properly filtered projects');
    
    // Show the comp summary modal for circle search results
    showCompSetSummaryModal();
    
  } catch (error) {
    console.error('❌ Circle + filters search failed:', error);
    isLoading.value = false;
    
    // Fallback to client-side filtering
    console.log('🔄 Falling back to client-side circle filtering');
    loadMarkers(); // This will use the existing circle filtering in isInSelectedArea()
  }
}

function reloadMap(newLocation) {
  if (newLocation?.geometry) {
    // Center the map on the new location
    const lat = newLocation.geometry?.location ? newLocation.geometry.location.lat() : null;
    const lng = newLocation.geometry?.location ? newLocation.geometry.location.lng() : null;
    if (lat && lng) {
      mapCenter = [lat, lng]; // Update the map center coordinates
      const mapCenterLocation = new google.maps.LatLng(mapCenter[0], mapCenter[1]);
      map.setCenter(mapCenterLocation); // Set the map center to the new location
      
      // Determine appropriate zoom level based on location type
      let zoomLevel = 12; // Default zoom level for cities
      
      // Check if the location is a zip code
      const isZipCode = /^\d{5}(-\d{4})?$/.test(selectedPlaceName.value) || 
                        (newLocation.address_components && 
                         newLocation.address_components.some(component => 
                           component.types.includes('postal_code')));
      
      if (isZipCode) {
        zoomLevel = 15; // Zoom in more for zip codes to focus on the specific area
        
        // If it's a zip code, we might want to draw a circle around it to highlight the area
        if (drawnCircle) {
          drawnCircle.setMap(null); // Remove any existing circle
        }
        
        // Create a new circle around the zip code area (approximately 2 miles radius for a zip code)
        const zipCodeCircle = new google.maps.Circle({
          strokeColor: '#4A5FD5',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#4A5FD5',
          fillOpacity: 0.15,
          map: map,
          center: mapCenterLocation,
          radius: metersInMile * 2, // 2 miles radius for a zip code
          editable: true,
          draggable: true
        });
        
        // Set the circle as the drawn circle and update the map query
        moveCircle(zipCodeCircle);
      } else if (newLocation.address_components) {
        // Adjust zoom based on address component types
        const hasStreet = newLocation.address_components.some(component => 
          component.types.includes('route'));
        const hasNeighborhood = newLocation.address_components.some(component => 
          component.types.includes('neighborhood') || component.types.includes('sublocality'));
        const isCity = newLocation.address_components.some(component => 
          component.types.includes('locality'));
        const isState = newLocation.address_components.some(component => 
          component.types.includes('administrative_area_level_1'));
          
        if (hasStreet) {
          zoomLevel = 16; // Street level
        } else if (hasNeighborhood) {
          zoomLevel = 14; // Neighborhood level
        } else if (isCity) {
          zoomLevel = 12; // City level
        } else if (isState) {
          zoomLevel = 8; // State level
        }
      }
      
      map.setZoom(zoomLevel);
      compSetStore.setZoom(zoomLevel); // Update the store
      
      // Update the map query to reflect the new location
      // Always set center point query - polygon bounds will override this if needed
      const mapQuery = mapCenter ? `location=${mapCenter.join(',')}` : null;
      compSetStore.setMapQuery(mapQuery);
      console.log('🎯 reloadMap: Setting center point query:', mapQuery);
    }

    // Only reinitialize the circle drawing component if we didn't create a circle for a zip code
    if (!drawnCircle) {
      circleDrawingComponent(); // Reinitialize the circle drawing component
    }
  }
}

const navigateToProjectSummary = (project) => {
  if (project && project.id) {
    // Open the project summary in a new tab
    window.open(`/portfolio/project-summary/${project.id}`, '_blank');
  } else {
    // Set an error if the project ID is not found
    errorStore.setError('Error', 'An error occurred. Project ID not found', 'error');
  }
};

async function fetchProjectImage(project) {
  // Fetch the image for the given project and update the project's image_url property
  const renderedImage = await fetchImage(project.id);
  project.image_url = renderedImage;
  return project;
}

async function loadMarkers() {
  // Get projects from compSetStore, filtered by scope if scope mode is active
  let data = compSetStore.includedProjects;

  if (compSetStore.getUseScopeSearch && compSetStore.getScopeFilteredProjectIds && compSetStore.getScopeFilteredProjectIds.length > 0) {
    const scopeIds = new Set(compSetStore.getScopeFilteredProjectIds.map(String));
    data = data.filter(p => p && scopeIds.has(String(p.id)));
  }

  // Clear any existing markers on the map
  clearOldMarkers();
  
  // Only proceed if we have valid data
  if (!data || !Array.isArray(data)) {
    console.warn('No valid project data available');
    return;
  }
  
  if (data.length === 0) {
    console.log('No projects to display on map');
    // Don't add a "no projects" marker as it can be confusing
    return;
  } else {
    console.log(`Loading ${data.length} projects on map`);
    // For each project, fetch its image and process it to add a marker on the map
    for (const project of data) {
      if (project && project.id) {
        try {
          await fetchProjectImage(project);
          processProject(project);
        } catch (error) {
          console.error(`Error processing project ${project.id}:`, error);
        }
      }
    }
  }
}

/**
 * Clears all existing markers and info windows from the map.
 * Closes all open info windows, removes all markers from the map,
 * and clears the markers and info windows arrays.
 */
function clearOldMarkers() {
  console.log('Clearing all markers from map');
  
  // Close all info windows
  if (infoWindows && infoWindows.length > 0) {
    infoWindows.forEach(infoWindow => {
      if (infoWindow) infoWindow.close();
    });
  }
  
  // Remove all markers from the map
  if (markers && markers.length > 0) {
    markers.forEach(marker => {
      if (marker) marker.setMap(null);
    });
  }
  
  // Reset the current info window
  if (currentInfoWindow) {
    currentInfoWindow.close();
    currentInfoWindow = null;
  }
  
  // Clear the arrays
  markers = [];
  infoWindows = [];
}

let currentInfoWindow = null;

function processProject(project) {
  // Get the location of the project
  const location = getLocation(project);
  if (!location) return;

  // Check if the project is within the selected area
  const inSelectedArea = isInSelectedArea(location);

  // Create a marker and an info window for the project
  const { marker, markerContent } = createMarker(location, inSelectedArea, project);
  const infoWindow = createInfoWindow(project, inSelectedArea);

  let hoverTimeout;

  // Add hover events directly to the marker content (same as PortfolioView)
  markerContent.addEventListener('mouseenter', () => {
    // Only show hover if no info window is currently open
    if (!currentInfoWindow) {
      clearTimeout(hoverTimeout);
      hoverTimeout = setTimeout(() => {
        // Open the full detailed info window on hover
        infoWindow.open({
          anchor: marker,
          map,
          shouldFocus: false,
        });
        currentInfoWindow = infoWindow;
      }, 300); // Short delay for better UX
    }
  });

  markerContent.addEventListener('mouseleave', () => {
    clearTimeout(hoverTimeout);
    // Don't close if user moves to the info window itself
    // Info window will stay open until user moves mouse away completely
  });

  markerContent.addEventListener('click', () => {
    clearTimeout(hoverTimeout);
    
    // Click keeps popup open (sticky) or closes it if already open
    if (currentInfoWindow === infoWindow) {
      // Close if already open
      currentInfoWindow.close();
      currentInfoWindow = null;
    } else {
      // Close any other open info window
      if (currentInfoWindow) {
        currentInfoWindow.close();
      }
      
      // Open this marker's info window and make it sticky
      infoWindow.open({
        anchor: marker,
        map,
        shouldFocus: true,
      });

      currentInfoWindow = infoWindow;
    }
  });

  // Add the marker and info window to their respective arrays
  markers.push(marker);
  infoWindows.push(infoWindow);

  return inSelectedArea;
}

function getLocation(project) {
  // Parse latitude and longitude from the project data
  const lat = parseFloat(project.latitude);
  const lng = parseFloat(project.longitude);
  if (isNaN(lat) || isNaN(lng)) {
    console.error('Invalid latitude or longitude:', project);
    return null;
  }
  return { lat, lng };
}

function isInSelectedArea(location) {
  // TEMPORARY: Backend location filtering is broken, so use client-side polygon filtering
  if (window.activePolygon && window.activePolygon.length > 0) {
    const inPolygon = pointInPolygon(location, window.activePolygon);
    return inPolygon;
  }
  
  // If we have a drawn circle, use circle filtering (user-drawn circle)
  if (drawnCircle && mapCenter && mapCenter.length >= 2) {
    const mapCenterLocation = new google.maps.LatLng(mapCenter[0], mapCenter[1]);
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(location.lat, location.lng),
      mapCenterLocation
    );
    const inCircle = distance <= mapRadiusMeters;
    return inCircle;
  } 
  
  // If we have geographic bounds, use bounds filtering
  if (mapBounds && mapBounds.length === 4) {
    // mapBounds format: [south, west, north, east]
    const south = mapBounds[0];
    const west = mapBounds[1]; 
    const north = mapBounds[2];
    const east = mapBounds[3];
    
    const latInRange = inRange(location.lat, south, north);
    const lngInRange = inRange(location.lng, west, east);
    const inBounds = latInRange && lngInRange;
    return inBounds;
  }
  
  // No filtering - show all projects
  return true;
}

function createMarker(location, inSelectedArea, project) {
  // Parse the SVG string to create an SVG element
  const parser = new DOMParser();
  const markerContent = parser.parseFromString(
    inSelectedArea ? pinSvgString : fadedPinSvgString,
    "image/svg+xml"
  ).documentElement;

  // Create a new marker with the SVG content and return both marker and content
  const marker = new google.maps.marker.AdvancedMarkerElement({
    position: location,
    map: map,
    content: markerContent
  });
  
  return { marker, markerContent };
}

// Helper function to get primary image URL from project's documents array (same as PortfolioView)
const getPrimaryImageUrl = (project) => {
  // Skip the old default image if present
  if (project.image_url && project.image_url !== '/img/property1.jpg') {
    return project.image_url;
  }
  
  // Look for primary image in documents array
  if (project.documents && Array.isArray(project.documents)) {
    const primaryDoc = project.documents.find(doc => doc.is_primary === true);
    if (primaryDoc && primaryDoc.id) {
      // Use ABSOLUTE URL for Google Maps info windows (relative URLs don't work)
      const imageUrl = window.location.hostname === 'localhost' 
        ? `http://localhost:3000/api/v1/documents/${primaryDoc.id}/file`
        : `${window.location.protocol}//${window.location.host}/api/v1/documents/${primaryDoc.id}/file`;
      return imageUrl;
    }
  }
  
  // Default placeholder if no image found
  return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect width="150" height="150" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
};

// Create a new info window with the project content (matching PortfolioView style)
function createInfoWindow(project, inSelectedArea) {
  const projectImageUrl = getPrimaryImageUrl(project);
  const hasRealImage = projectImageUrl && !projectImageUrl.includes('data:image/svg');
  
  const imageHtml = hasRealImage 
    ? `<div style="flex-shrink: 0; margin-right: 10px;">
         <img id="project-image-${project.id}" 
              src="${projectImageUrl}"
              style="width:100px; height:75px; object-fit: cover; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s; border-radius: 4px; background: #5a8fc4;" 
              onmouseover="this.style.borderColor='#ffffff'" 
              onmouseout="this.style.borderColor='transparent'" 
              title="Click to view project details"
              onerror="this.style.display='none';">
       </div>`
    : '';
  
  const infoWindow = new google.maps.InfoWindow({
    content: `<div style="background: ${inSelectedArea ? '#3b73a8' : '#FEB465'}; color: #fff; font-size: 12px; display: flex; align-items: flex-start; padding: 8px; width: 320px; min-height: 120px;">
                ${imageHtml}
                <div style="flex: 1; min-width: 0;">
                  <div style="margin-bottom: 8px;">
                    <a id="project-link-${project.id}" href="javascript:void(0);" 
                       style="color: #ffffff; text-decoration: underline; cursor: pointer; font-weight: bold; font-size: 13px; line-height: 1.2;">
                      ${project.name || project.address_line_1}
                    </a>
                  </div>
                  <div style="display: grid; grid-template-columns: auto 1fr; gap: 4px 8px; font-size: 11px;">
                    <div style="color: #ccc;">Address:</div>
                    <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${project.address_line_1 || '-'}</div>
                    
                    <div style="color: #ccc;">Total GSF:</div>
                    <div>${project.total_gsf ? numberFormat(project.total_gsf, 0) : '-'}</div>
                    
                    <div style="color: #ccc;">Units:</div>
                    <div>${project.total_num_units ? numberFormat(project.total_num_units, 0) : '-'}</div>
                    
                    <div style="color: #ccc;">Budget:</div>
                    <div style="font-weight: bold;">${project.total_current_budget ? currencyFormat(project.total_current_budget) : '-'}</div>
                    
                    <div style="color: #ccc;">Type:</div>
                    <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${project.project_construction_types_s ? project.project_construction_types_s[0] : '-'}</div>
                    
                    <div style="color: #ccc;">Status:</div>
                    <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${project.project_status || '-'}</div>
                  </div>
                </div>
              </div>`,
  });

  infoWindow.addListener('domready', () => {
    const linkElement = document.getElementById(`project-link-${project.id}`);
    if (linkElement) {
      linkElement.addEventListener('click', () => navigateToProjectSummary(project));
    }
    
    // Image src is now set directly in HTML, just add click handler
    const imgElement = document.getElementById(`project-image-${project.id}`);
    if (imgElement && hasRealImage) {
      imgElement.addEventListener('click', () => navigateToProjectSummary(project));
    }
  });

  return infoWindow;
}

function addNoProjectsMarker() {
  // Check if map is initialized
  if (!map) {
    console.warn('Map not initialized yet');
    return;
  }

  try {
    // Get the center coordinates of the map
    let centerLat, centerLng;
    
    if (mapCenter && mapCenter.length > 0) {
      centerLat = mapCenter[0];
      centerLng = mapCenter[1];
    } else if (map.getCenter) {
      centerLat = map.getCenter().lat();
      centerLng = map.getCenter().lng();
    } else {
      // Default to a fallback location if no center is available
      centerLat = 37.7749; // San Francisco latitude
      centerLng = -122.4194; // San Francisco longitude
      console.warn('Using fallback map center coordinates');
    }
    
    const mapCenterLocation = new google.maps.LatLng(centerLat, centerLng);

    // Create a new marker at the center of the map
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: mapCenterLocation,
      map: map,
    });

    // Create an info window with a message indicating no available projects
    const infoWindow = new google.maps.InfoWindow({
      content: `<h1>No available projects here</h1>`,
    });
    // Add a click listener to the marker to open the info window
    marker.addListener('click', () => {
      infoWindow.open({
        anchor: marker,
        map,
        shouldFocus: false,
      });
    });

    // Add the marker and info window to their respective arrays
    markers.push(marker);
    infoWindows.push(infoWindow);
  } catch (error) {
    console.error('Error adding no projects marker:', error);
  }
}

// Sidebar Functions
let isLeftOpen = ref(true);
let isRightOpen = ref(false);
// FORCE: Never show the second panel to eliminate white space issue
let isRightVisible = ref(true); // Always show CompSummary panel (was false)
console.log('🔥🔥🔥 INITIAL isRightVisible:', isRightVisible.value);
watch(isRightVisible, (newVal) => {
  console.log('🔥🔥🔥 isRightVisible CHANGED to:', newVal);
  console.trace('isRightVisible change stack trace');
});
watch(isRightOpen, (newVal) => {
  console.log('🔥 DEBUG isRightOpen changed to:', newVal);
});
function openLeftModal() {
  // REMOVED: Don't toggle here - CompSearch.toggleSidebar() already handles the toggle
  // This function is called via @resize-sidebar-comp-search event, which should only trigger map resize
  // isLeftOpen.value = !isLeftOpen.value;
  
  // Just trigger map resize without toggling the sidebar state
  console.log('🗺️ Sidebar toggled - triggering map resize');
}

// Handle comp search submit events
function handleCompSearchSubmit(eventData) {
  console.log('🔍 Handling comp search submit:', eventData);
  
  if (eventData && eventData.type === 'circle-with-filters') {
    // Apply circle search with current filters - this will handle showing the modal
    console.log('🎯 Applying circle search with filters');
    applyCircleWithFilters();
  } else {
    // Regular search - show the comp set summary modal which triggers intelligent search
    showCompSetSummaryModal();
  }
}

function showCompSetSummaryModal() {
  console.log('🎯 Opening CompSummary panel');
  console.log('🎯 BEFORE: isRightVisible =', isRightVisible.value);
  isRightVisible.value = true;
  console.log('🎯 AFTER: isRightVisible =', isRightVisible.value);
  
  // Open the panel immediately
  nextTick(() => {
    console.log('🎯 In nextTick: isRight Open =', isRightOpen.value);
    isRightOpen.value = true;
    console.log('🎯 After setting: isRightOpen =', isRightOpen.value);
    if (compSummaryRef.value) {
      // Force the modal to show
      if (compSummaryRef.value.showModal !== undefined) {
        compSummaryRef.value.showModal = true;
      }
      // Try to trigger sync if method exists
      if (typeof compSummaryRef.value.syncProjects === 'function') {
        compSummaryRef.value.syncProjects();
      }
    }
  });
}

// Expose function globally for debugging
window.showCompSetSummaryModal = showCompSetSummaryModal;

function toggleRightModal() {
  isRightOpen.value = !isRightOpen.value;
}

// Handle filters cleared event
function handleFiltersCleared() {
  console.log('🧹 Filters cleared - closing panels and showing all pins');
  
  // Close the CompSummary panel
  closeRightPanel();
  
  // Show all pins
  showAllPins();
}

// Function to close the CompSummary panel (not the DiDiAi panel)
function closeRightPanel() {
  console.log('Closing CompSummary panel');
  
  // Hide the entire CompSummary modal
  isRightVisible.value = false;
  
  // Also close the CompSummary component itself
  if (compSummaryRef.value) {
    // Access the showModal property if it exists
    if (compSummaryRef.value.showModal !== undefined) {
      compSummaryRef.value.showModal = false;
    }
  }
  
  // REMOVED: Nuclear fix that was preventing CompSummary from showing
  // nextTick(() => {
  //   const allCompSummaryElements = document.querySelectorAll('.side-panel-left, #comp-summary-modal, [data-test-id="comp-summary-panel"]');
  //   allCompSummaryElements.forEach(el => {
  //     el.style.display = 'none';
  //     el.style.visibility = 'hidden';
  //     el.style.width = '0px';
  //     el.style.opacity = '0';
  //   });
  // });
}

// Function to reset the map and optionally apply new bounds/filters
function resetMap(options = null) {
  console.log('Resetting map with options:', options);
  
  // Handle centerOnly case - just center the map without clearing anything
  if (options?.centerOnly) {
    console.log('🎯 CENTER ONLY: Just centering map on:', options.center);
    if (map && options.center) {
      map.setCenter(new google.maps.LatLng(options.center.lat, options.center.lng));
      if (options.zoom) {
        map.setZoom(options.zoom);
      }
    }
    return; // Exit early, don't clear anything
  }
  
  // Set flag to prevent bounds_changed from triggering during reset
  isMapProgrammaticallyUpdating = true;
  
  // Close the right panel first
  closeRightPanel();
  
  // Then clear all markers and info windows (only if not centerOnly)
  if (!options?.clearMarkers === false) {
    clearOldMarkers();
  }
  
  // Clear any existing overlays ONLY if preserveCircle is not set
  if (drawnCircle && !options?.preserveCircle) {
    drawnCircle.setMap(null);
    drawnCircle = null;
  }
  
  if (window.drawnBounds) {
    window.drawnBounds.setMap(null);
    window.drawnBounds = null;
  }
  
  if (window.drawnPolygon) {
    window.drawnPolygon.setMap(null);
    window.drawnPolygon = null;
  }
  
  // Clear administrative boundary overlay
  if (window.adminBoundary) {
    window.adminBoundary.setMap(null);
    window.adminBoundary = null;
  }
  
  // Clear boundary markers
  if (window.boundaryMarkers) {
    window.boundaryMarkers.forEach(marker => marker.setMap(null));
    window.boundaryMarkers = null;
  }
  
  // Clear active polygon for filtering  
  window.activePolygon = null;
  
  // Clear any drawing manager if no new options or if we're applying bounds
  if ((!options || options.bounds) && drawingManager) {
    drawingManager.setMap(null);
    drawingManager = null;
  }
  
  if (map) {
    if (options) {
      // Apply new search parameters
      if (options.center) {
        map.setCenter(new google.maps.LatLng(options.center.lat, options.center.lng));
      }
      if (options.zoom) {
        map.setZoom(options.zoom);
      }
      
      // Handle polygon overlay (for complex geographic areas like Manhattan)
      if (options.polygon && options.polygonType === 'geographic') {
        drawGeographicPolygon(options.polygon, options.areaName);
        updateMapQueryForPolygon(options.polygon);
      }
      // Handle administrative areas with Google's red dotted boundary lines
      else if (options.bounds && options.boundsType === 'administrative' && options.showAdministrativeBoundaries) {
        console.log('🎯 RESETMAP: Showing Google administrative boundaries for:', options.placeName, options.administrativeArea);
        console.log('🎯 RESETMAP: Place ID:', options.placeId);
        console.log('🎯 RESETMAP: Bounds:', options.bounds);
        
        // Enable Google's administrative boundary layer (red dotted lines)
        // This function will set the proper polygon bounds via updateMapQueryForPolygon
        enableGoogleAdministrativeBoundaries(options.administrativeArea, options.placeId);
      }
      // Handle bounds overlay (for simple geographic areas)
      else if (options.bounds && options.boundsType === 'geographic') {
        drawGeographicBounds(options.bounds);
        updateMapQueryForBounds(options.bounds);
      }
      // Handle circle overlay (for radius searches)
      else if (options.circle) {
        drawSearchCircle(options.circle);
        updateMapQueryForCircle(options.circle);
      }
    } else {
      // Reset to default state
      compSetStore.resetMapState();
      compSetStore.setMapQuery(null);
      compSetStore.setMapExpandedQuery(null);
      
      // Reset the map center and zoom level to default values
      map.setCenter(new google.maps.LatLng(compSetStore.center.lat, compSetStore.center.lng));
      map.setZoom(compSetStore.zoom);
      
      // Re-initialize the drawing manager
      initializeDrawingManager();
    }
    
    // Force a redraw of the map
    google.maps.event.trigger(map, 'resize');
  } else {
    console.error('Map not initialized');
    // Try to reinitialize the map
    loadGoogleMaps();
  }
  
  // Clear the flag after reset is complete
  setTimeout(() => {
    isMapProgrammaticallyUpdating = false;
    // Load markers with the new filtering
    loadMarkers();
  }, 300); // Shorter delay for better responsiveness
}

// Function to show all pins on the map
function showAllPins() {
  console.log('Showing all pins and resetting map');
  
  // Set flag to prevent cascading reloads
  isMapProgrammaticallyUpdating = true;
  
  // First, completely reset the map
  resetMap();
  
  // Explicitly close the right panel again to be sure
  closeRightPanel();
  
  // Clear the store's location and comp set data
  compSetStore.setLocation(null);
  compSetStore.clearCompSetData();
  
  // Clear any white overlays or loading indicators that might be remnants
  const loadingIndicators = document.querySelectorAll('.loading-indicator');
  loadingIndicators.forEach(indicator => {
    if (document.body.contains(indicator)) {
      document.body.removeChild(indicator);
    }
  });
  
  // Clear any overlay elements that might cause white remnants
  const overlayElements = document.querySelectorAll('.overlay-spinner, .spinner-overlay, .modal-overlay, .comp-summary-modal');
  overlayElements.forEach(overlay => {
    overlay.style.display = 'none';
    overlay.classList.remove('open');
  });
  
  // Show loading indicator
  isLoading.value = true;
  
  // Get the API base URL and auth token
  const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/";
  const authStore = useAuthStore();
  const authToken = authStore.getAuthTokenHeader;
  
  // Use the axiosQuery utility function to fetch all projects
  axiosQuery(
    `${apiBase}projects/released_projects`,
    authToken,
    (data) => {
      console.log(`Fetched ${data.length} projects`);
      
      // Update the store with the fetched projects
      compSetStore.setAllProjects(data);
      compSetStore.setIncludedProjects(data);
      
      // Set all projects as selected
      const selectedProjects = Object.fromEntries(data.map(project => [project.id, true]));
      compSetStore.setSelectedProjects(selectedProjects);
      
      // Clear all existing markers
      clearOldMarkers();
      
      // Add markers for all projects
      if (data.length > 0) {
        // Process each project to add a marker
        data.forEach(project => {
          processProject(project);
        });
        
        // After adding all markers, fit the map to show all of them
        if (markers.length > 0) {
          const bounds = new google.maps.LatLngBounds();
          
          markers.forEach(marker => {
            if (marker.position) {
              bounds.extend(marker.position);
            }
          });
          
          if (!bounds.isEmpty()) {
            map.fitBounds(bounds);
            
            // Set a consistent zoom level for all pins view
            google.maps.event.addListenerOnce(map, "idle", () => {
              // Use a fixed zoom level that shows all pins without being too zoomed in or out
              map.setZoom(5); // Zoom level 5 is good for showing the entire US
            });
          }
        }
      }
      
      // Hide loading indicator
      isLoading.value = false;
      
      // Clear the programmatic update flag after everything is loaded
      setTimeout(() => {
        isMapProgrammaticallyUpdating = false;
      }, 1000); // Longer delay to let all map operations complete
    },
    errorStore,
    'Fetch Projects'
  );
}

// Function to draw geographic bounds (rectangles) on the map
function drawGeographicBounds(bounds) {
  console.log('Drawing geographic bounds:', bounds);
  
  // Create a rectangle overlay for the geographic bounds
  const boundsRect = new google.maps.Rectangle({
    bounds: new google.maps.LatLngBounds(
      new google.maps.LatLng(bounds.southwest.lat, bounds.southwest.lng),
      new google.maps.LatLng(bounds.northeast.lat, bounds.northeast.lng)
    ),
    fillColor: '#4A8FD0',
    fillOpacity: 0.2,
    strokeColor: '#4A5FD5',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    map: map
  });
  
  // Store reference globally for cleanup
  window.drawnBounds = boundsRect;
  
  // Update global bounds variables for project filtering  
  // Format: [north_lat, east_lng, south_lat, west_lng] to match inRange usage:
  // inRange(location.lat, mapBounds[0], mapBounds[2]) checks lat between north and south
  // inRange(location.lng, mapBounds[1], mapBounds[3]) checks lng between east and west
  mapBounds = [
    bounds.northeast.lat,   // [0] north latitude
    bounds.northeast.lng,   // [1] east longitude  
    bounds.southwest.lat,   // [2] south latitude
    bounds.southwest.lng    // [3] west longitude
  ];
  
  console.log('Updated mapBounds for filtering:', {
    bounds: mapBounds,
    area: `Lat: ${mapBounds[2]} to ${mapBounds[0]}, Lng: ${mapBounds[3]} to ${mapBounds[1]}`
  });
}

// Function to draw search circle on the map
function drawSearchCircle(circleOptions) {
  console.log('Drawing search circle:', circleOptions);
  
  // Create circle overlay
  const searchCircle = new google.maps.Circle({
    center: new google.maps.LatLng(circleOptions.center.lat, circleOptions.center.lng),
    radius: circleOptions.radius,
    fillColor: '#4A8FD0',
    fillOpacity: 0.2,
    strokeColor: '#4A5FD5',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    map: map
  });
  
  // Store reference globally for cleanup and project filtering
  drawnCircle = searchCircle;
  mapRadiusMeters = circleOptions.radius;
  mapCenter = [circleOptions.center.lat, circleOptions.center.lng, circleOptions.radius];
}

// Function to update map query for bounds-based filtering
function updateMapQueryForBounds(bounds) {
  const boundsQuery = `location=${bounds.northeast.lat},${bounds.northeast.lng},${bounds.southwest.lat},${bounds.southwest.lng}`;
  compSetStore.setMapQuery(boundsQuery);
  compSetStore.setMapExpandedQuery(boundsQuery);
  console.log('Updated map query for bounds:', boundsQuery);
}

// Function to update map query for circle-based filtering
function updateMapQueryForCircle(circle) {
  const circleQuery = `location=${circle.center.lat},${circle.center.lng},${circle.radius}`;
  compSetStore.setMapQuery(circleQuery);
  compSetStore.setMapExpandedQuery(circleQuery);
  console.log('Updated map query for circle:', circleQuery);
}

// Function to draw geographic polygon on the map
function drawGeographicPolygon(polygonCoords, areaName) {
  console.log(`Drawing geographic polygon for ${areaName}:`, polygonCoords);
  
  // Convert coordinates to Google Maps LatLng objects
  const polygonPath = polygonCoords.map(coord => 
    new google.maps.LatLng(coord.lat, coord.lng)
  );
  
  // Create a polygon overlay
  const polygon = new google.maps.Polygon({
    paths: polygonPath,
    fillColor: '#4A8FD0',
    fillOpacity: 0.2,
    strokeColor: '#4A5FD5',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    map: map
  });
  
  // Store reference globally for cleanup
  window.drawnPolygon = polygon;
  
  // Store polygon coordinates for point-in-polygon testing
  window.activePolygon = polygonCoords;
  
  console.log(`Polygon drawn for ${areaName} with ${polygonCoords.length} points`);
}

// Function to update map query for polygon-based filtering
function updateMapQueryForPolygon(polygon) {
  // Store the polygon for precise filtering
  window.activePolygon = polygon;

  // Send actual polygon coordinates for precise PostGIS filtering
  const polygonCoords = polygon.map(p => `${p.lat},${p.lng}`).join('|');
  
  // Use "polygon=" format to distinguish from rectangular "location=" format
  const polygonQuery = `polygon=${polygonCoords}`;
  console.log('🔥 BEFORE setMapQuery - current mapQuery:', compSetStore.getMapQuery);
  console.log('🔥 BEFORE setMapQuery - current getQuery:', compSetStore.getQuery);
  compSetStore.setMapQuery(polygonQuery);
  compSetStore.setMapExpandedQuery(polygonQuery);
  console.log('🔥 AFTER setMapQuery - new mapQuery:', compSetStore.getMapQuery);
  console.log('🔥 AFTER setMapQuery - new getQuery:', compSetStore.getQuery);
  console.log('🎯 Updated map query for polygon filtering:', polygonQuery);
  console.log('🎯 Polygon coordinates:', polygonCoords);
}

// Get precise polygon boundaries from Google Maps
function enableGoogleAdministrativeBoundaries(administrativeArea, placeId) {
  console.log(`🎯 Getting precise boundary for: ${administrativeArea} (Place ID: ${placeId})`);

  const geocoder = new google.maps.Geocoder();
  geocoder.geocode({ placeId: placeId }, (results, status) => {
    if (status === 'OK' && results[0]) {
      const place = results[0];
      console.log('✅ Geocoding result:', place);

              // Check if Google provides detailed geometry (rare but possible)
        if (place.geometry && place.geometry.geometry) {
          console.log('💡 Found detailed geometry. Attempting complex polygon.');
          const success = drawComplexPolygon(place.geometry.geometry);
          if (!success) {
            console.log('💡 Complex polygon failed, using viewport fallback.');
            createRefinedPolygonFromViewport(place.geometry.viewport, administrativeArea);
          }
        }
        // Standard case: use viewport to create a refined shape
        else if (place.geometry && place.geometry.viewport) {
          console.log('💡 Using viewport to create refined octagon shape.');
          createRefinedPolygonFromViewport(place.geometry.viewport, administrativeArea);
        }
      // Ultimate fallback if no geometry is available
      else {
        console.warn('⚠️ No geometry or viewport available. Cannot draw boundary.');
      }
    } else {
      console.error('❌ Geocoding failed for Place ID:', placeId, 'Status:', status);
    }
  });
}

  // Draws a complex polygon from detailed geometry data
  function drawComplexPolygon(geometry) {
    console.log('🎯 Drawing complex polygon from geometry:', geometry);
    
    // Clear any previous boundary
    if (window.adminBoundary) {
      window.adminBoundary.setMap(null);
    }

    // Google Maps geometry objects are complex - let's handle them properly
    try {
      const paths = [];
      
      // Convert Google Maps geometry to simple coordinate array
      if (geometry && typeof geometry.getArray === 'function') {
        // This is likely a polygon with multiple paths
        const array = geometry.getArray();
        if (array && array.length > 0) {
          const outerPath = array[0]; // Get the outer boundary
          if (outerPath && typeof outerPath.getArray === 'function') {
            outerPath.getArray().forEach(latLng => {
              if (latLng && typeof latLng.lat === 'function' && typeof latLng.lng === 'function') {
                paths.push({ lat: latLng.lat(), lng: latLng.lng() });
              }
            });
          }
        }
      }

      if (paths.length > 3) {
        console.log(`✅ Creating complex polygon with ${paths.length} points`);
        
        const boundaryPolygon = new google.maps.Polygon({
          paths: paths,
          strokeColor: '#d93025',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#d93025',
          fillOpacity: 0.1,
          zIndex: 999,
          map: map,
          icons: [{
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 1,
              strokeColor: '#d93025',
              strokeWeight: 1
            },
            offset: '0',
            repeat: '10px'
          }]
        });
        
        window.adminBoundary = boundaryPolygon;
        console.log(`✅ Complex polygon drawn successfully`);
        
        // Update map query for filtering
        updateMapQueryForPolygon(paths);
      } else {
        console.log('⚠️ Not enough points for complex polygon, falling back to viewport');
        // Fallback to viewport-based polygon
        return false;
      }
    } catch (error) {
      console.error('❌ Error drawing complex polygon:', error);
      return false;
    }
    
    return true;
  }

// Create a refined, 8-sided polygon from a viewport
function createRefinedPolygonFromViewport(viewport, administrativeArea) {
  console.log('🎯 Creating refined 8-sided boundary for:', administrativeArea);

  const ne = viewport.getNorthEast();
  const sw = viewport.getSouthWest();

  const centerLat = (ne.lat() + sw.lat()) / 2;
  const centerLng = (ne.lng() + sw.lng()) / 2;
  
  // Use full radius for proper city coverage
  const latRadius = (ne.lat() - sw.lat()) / 2;
  const lngRadius = (ne.lng() - sw.lng()) / 2;

  // Create an octagon for a more natural shape
  const polygonCoords = [];
  for (let i = 0; i < 8; i++) {
    const angle = i * (Math.PI / 4); // 45 degrees per step
    polygonCoords.push({
      lat: centerLat + latRadius * Math.cos(angle),
      lng: centerLng + lngRadius * Math.sin(angle)
    });
  }

  const boundaryPolygon = new google.maps.Polygon({
    paths: polygonCoords,
    strokeColor: '#d93025',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#d93025',
    fillOpacity: 0.1,
    zIndex: 999,
    map: map,
    icons: [{
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 1,
        strokeColor: '#d93025',
        strokeWeight: 1
      },
      offset: '0',
      repeat: '10px'
    }]
  });

  if (window.adminBoundary) {
    window.adminBoundary.setMap(null);
  }
  window.adminBoundary = boundaryPolygon;
  console.log(`✅ Refined octagon boundary drawn for ${administrativeArea}.`);
  console.log('🎯 Polygon should be visible on map with coords:', polygonCoords);

  // Update map query for filtering
  updateMapQueryForPolygon(polygonCoords);
}



// Point-in-polygon test using ray casting algorithm
function pointInPolygon(point, polygon) {
  const x = point.lat;
  const y = point.lng;
  
  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lat;
    const yi = polygon[i].lng;
    const xj = polygon[j].lat;
    const yj = polygon[j].lng;
    
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  
  return inside;
}
</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

.compsearch-page-container {
  display: flex;
  flex-direction: row;
  align-items: start;
  height: 100vh;
  margin-top: 53px;
  font-family: 'Inter';
  position: relative;
}

.left-panels-container {
  display: flex;
  flex-direction: row;
  height: 100vh;
  position: relative;
  /* CRITICAL FIX: Only take up space of visible children */
  width: fit-content !important;
  max-width: 310px !important;
}

.comp-search-modal {
  /* CRITICAL FIX: Don't set width here - let the child CompSearch component handle it */
  overflow: visible;
  width: auto !important;
}

.comp-search-modal.open {
  /* CRITICAL FIX: Don't set width here - CompSearch already has 310px */
  width: auto !important;
}

.map-container {
  flex: 1;
  width: 100%;
  height: 100vh;
  margin-left: 10px;
  transition: margin 0.3s ease;
  position: relative;
}

.map-container.left-open {
  margin-left: 320px; /* Space for first panel */
}

.map-container.both-open {
  margin-left: 970px; /* Space for both panels */
}

/* Ensure map controls are always visible */
:deep(.gmnoprint), :deep(.gm-style-cc), :deep(.gm-control-active), :deep(.gm-svpc), :deep(.gm-fullscreen-control), :deep(.gm-style-mtc) {
  z-index: 1000 !important;
}

/* Make drawing tools more visible */
:deep(.gm-style-mtc), :deep(.gm-style-mtc > div), :deep(.gm-style-mtc button) {
  z-index: 1000 !important;
}

/* Make drawing tools bigger */
:deep(.gm-style > div > div > div > button) {
  transform: scale(1.3) !important;
  margin: 5px !important;
}

:deep(.gm-style > div > div > div > button > img) {
  width: 20px !important;
  height: 20px !important;
}

:deep(.gm-style > div > div > div > button > span) {
  font-size: 14px !important;
  font-weight: bold !important;
}

/* Ensure drawing manager controls are visible */
:deep(.gm-style > div > div > div:last-child > div) {
  z-index: 1000 !important;
}

/* CompSummary modal wrapper - FORCE HIDDEN since isRightVisible is always false */
/* REMOVED: Nuclear fix CSS that was force-hiding CompSummary with !important */
/* .comp-summary-modal {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
  visibility: hidden !important;
  opacity: 0 !important;
  position: absolute !important;
  pointer-events: none !important;
} */

/* REMOVED: Force hide that was preventing side-panel-left from showing */
/* .side-panel-left {
  display: none !important;
} */

/* DISABLED: Don't show side-panel-left even when parent exists */
/* .comp-summary-modal .side-panel-left {
  display: flex !important;
} */

/* Prevent white remnants from overlays */
.loading-indicator {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 15px 30px;
  border-radius: 5px;
  z-index: 9999;
  font-weight: bold;
}

/* Hide any overlay elements that might cause visual remnants */
.overlay-spinner, .spinner-overlay, .modal-overlay {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.overlay-spinner.hidden, 
.spinner-overlay.hidden, 
.modal-overlay.hidden {
  display: none !important;
  visibility: hidden !important;
}
</style>